import React, { useState, useEffect } from "react";
import { FaBullhorn, FaMoneyBillWave, FaUsers, FaChartLine, FaCreditCard, FaCalendarAlt } from "react-icons/fa";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Pie, Line } from "react-chartjs-2";
import { getUserCampaigns } from "../../services/Campaigns.ts";
import { themes } from "../../theme.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

interface CampaignStats {
  id: number;
  title: string;
  activeUsers: number;
  totalPayments: number;
  subscriptionStatus: string;
  startDate: string;
  endDate: string;
  is_live?: boolean;
  campaign_budget?: string;
  budget_left?: string;
  created_at?: string;
  deadline?: string;
}

const Stats: React.FC = () => {
  const { themeName } = useTheme();
  const t = (themes as any)[themeName].colors as Record<string, string>;

  const [campaigns, setCampaigns] = useState<CampaignStats[]>([]);
  const [stats, setStats] = useState({
    activeCampaigns: 0,
    totalPayments: 0,
    activeSubscriptions: 0,
    totalUsers: 0
  });
  const [loading, setLoading] = useState(true);

  const rawData = localStorage.getItem('user');
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;
  const id = cred?.user?.id;


  const getUserCamp = async () => {
    try {
      const response = await getUserCampaigns(token, id);
      if (response && response.data) {
        setCampaigns(response?.data || []);

        setStats({
          activeCampaigns: response.data.length,
          totalPayments: response.data.reduce((sum: number, campaign: CampaignStats) =>
            sum + parseFloat(campaign.campaign_budget || "0"), 0
          ),
          activeSubscriptions: response.data.filter((camp: CampaignStats) => camp.subscriptionStatus === "Active").length,
          totalUsers: 0 // Placeholder, update as needed
        });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserCamp();
  }, []);

  // Calculate live vs non-live campaigns
  const liveCampaigns = campaigns?.filter(campaign => campaign.is_live)?.length;
  const nonLiveCampaigns = campaigns?.length - liveCampaigns;

  // Prepare data for charts
  const barChartData = {
    labels: campaigns?.map(campaign => campaign?.title),
    datasets: [
      {
        label: "Campaign Budget",
        data: campaigns?.map(campaign => parseFloat(campaign.campaign_budget || "0")),
        backgroundColor: t.secondary,
      },
    ],
  };

  const pieChartData = {
    labels: ["Live Campaigns", "Non-Live Campaigns"],
    datasets: [
      {
        data: [liveCampaigns, nonLiveCampaigns],
        backgroundColor: [t.success, t.warning],
      },
    ],
  };

  const timelineData = {
    labels: campaigns?.map(campaign => campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : ""),
    datasets: [
      {
        label: "Campaign Budget",
        data: campaigns?.map(campaign => parseFloat(campaign.campaign_budget || "0")),
        borderColor: t.accent,
        backgroundColor: t.accent + '22',
        tension: 0.1,
      },
    ],
  };

  // Calculate total budget
  const totalBudget = campaigns?.reduce((sum, campaign) =>
    sum + parseFloat(campaign.campaign_budget || "0"), 0
  );

  // Calculate total spent
  const totalSpent = campaigns?.reduce((sum, campaign) =>
    sum + (campaign.budget_left ?
      parseFloat(campaign.campaign_budget || "0") - parseFloat(campaign.budget_left) : 0), 0
  );

  // Prepare data for charts
  const budgetData = {
    labels: ["Total Budget", "Total Spent", "Remaining"],
    datasets: [
      {
        label: "Budget Distribution",
        data: [totalBudget, totalSpent, totalBudget - totalSpent],
        backgroundColor: [t.primary, t.primary, t.primary + '44'],
      },
    ],
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: t.background }}>
        <div
          className="animate-spin rounded-full h-16 w-16 border-4 border-t-primary"
          style={{ borderColor: `${t.primary} ${t.primary} ${t.primary} transparent` }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden" style={{ background: t.background, minHeight: '100vh' }}>
      {/* Header */}
      <header
        className="p-4 flex-shrink-0"
        style={{
          background: themeName === 'light' ? `linear-gradient(to right, ${t.primary}, ${t.success})` : t.primary,
        }}
      >
        <div>
          <h2 className="text-xl font-bold mb-4" style={{ color: t.text }}>Campaign Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="backdrop-blur-sm rounded-lg p-3" style={{ background: themeName === 'light' ? 'rgba(255,255,255,0.1)' : t.surface + '22' }}>
              <div className="flex items-center gap-2 mb-1">
                <FaBullhorn size={18} style={{ color: t.text }} />
                <h3 className="text-base font-semibold" style={{ color: t.text }}>Active Campaigns</h3>
              </div>
              <p className="text-2xl font-bold" style={{ color: t.text }}>{stats.activeCampaigns}</p>
            </div>
            <div className="backdrop-blur-sm rounded-lg p-3" style={{ background: themeName === 'light' ? 'rgba(255,255,255,0.1)' : t.surface + '22' }}>
              <div className="flex items-center gap-2 mb-1">
                <FaMoneyBillWave size={18} style={{ color: t.text }} />
                <h3 className="text-base font-semibold" style={{ color: t.text }}>Total Budget</h3>
              </div>
              <p className="text-2xl font-bold" style={{ color: t.text }}>Rs {stats.totalPayments}</p>
            </div>
            <div className="backdrop-blur-sm rounded-lg p-3" style={{ background: themeName === 'light' ? 'rgba(255,255,255,0.1)' : t.surface + '22' }}>
              <div className="flex items-center gap-2 mb-1">
                <FaUsers size={18} />
                <h3 className="text-base font-semibold">Active Influencer</h3>
              </div>
              <p className="text-2xl font-bold" style={{ color: t.text }}>{stats.totalUsers}</p>
            </div>
            <div className="backdrop-blur-sm rounded-lg p-3" style={{ background: themeName === 'light' ? 'rgba(255,255,255,0.1)' : t.surface + '22' }}>
              <div className="flex items-center gap-2 mb-1">
                <FaChartLine size={18} style={{ color: t.text }} />
                <h3 className="text-base font-semibold" style={{ color: t.text }}>Active Subscriptions</h3>
              </div>
              <p className="text-2xl font-bold" style={{ color: t.text }}>{stats.activeSubscriptions}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 p-6">
        <div>
          {/* Campaign Details Table */}
          <div className="rounded-xl shadow-lg overflow-hidden p-6 border mb-6" style={{ background: t.surface, borderColor: t.border }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: t.text }}>📋 Campaign Details</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead style={{ background: t.primary }}>
                  <tr>
                    <th className="p-3 text-left" style={{ color: t.inverseText }}>Campaign</th>
                    <th className="p-3 text-left" style={{ color: t.inverseText }}>Active Influencer</th>
                    <th className="p-3 text-left" style={{ color: t.inverseText }}>Budget</th>
                    <th className="p-3 text-left" style={{ color: t.inverseText }}>Status</th>
                    <th className="p-3 text-left" style={{ color: t.inverseText }}>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns?.map((campaign) => (
                    <tr
                      key={campaign.id}
                      className="border-b"
                      style={{
                        borderColor: t.border,
                        background: themeName === 'light' ? 'transparent' : t.surface
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeName === 'light' ? t.card : t.surface + '88'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = themeName === 'light' ? 'transparent' : t.surface}
                    >
                      <td className="p-3" style={{ color: t.text }}>{campaign.title}</td>
                      <td className="p-3" style={{ color: t.text }}>{campaign.activeUsers || "not defined"}</td>
                      <td className="p-3" style={{ color: t.text }}>Rs {campaign?.campaign_budget}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 rounded-full text-xs"
                          style={{
                            background: campaign.is_live ? t.success + '22' : t.error + '22',
                            color: campaign.is_live ? t.success : t.error
                          }}>
                          {campaign.is_live ? "Active" : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-3" style={{ color: t.text }}>
                        {campaign.created_at ? new Date(campaign.created_at).toLocaleDateString() : ""} - {campaign.deadline ? new Date(campaign.deadline).toLocaleDateString() : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bar Chart Card */}
            <div className="rounded-xl shadow-lg p-6 border" style={{ background: t.surface, borderColor: t.border }}>
              <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: t.text }}>
                📊
                <span className="ml-2">Campaign Performance</span>
              </h3>
              <div className="h-[300px]">
                <Bar
                  data={barChartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: "top" as const,
                        labels: { color: t.text },
                      },
                    },
                    scales: {
                      x: { ticks: { color: t.textSecondary } },
                      y: { ticks: { color: t.textSecondary } },
                    },
                  }}
                />
              </div>
            </div>

            {/* Pie Chart Card */}
            <div className="rounded-xl shadow-lg p-6 border" style={{ background: t.surface, borderColor: t.border }}>
              <h3 className="text-lg font-semibold mb-4 flex items-center" style={{ color: t.text }}>
                📈
                <span className="ml-2">Overall Distribution</span>
              </h3>
              <div className="h-[300px]">
                <Pie
                  data={pieChartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: "top" as const,
                        labels: { color: t.text },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>

          {/* Budget Distribution and Timeline Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {/* Budget Distribution */}
            <div className="p-4 rounded-lg shadow border" style={{ background: t.surface, borderColor: t.border }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: t.text }}>Budget Distribution</h3>
              <div className="h-[200px]">
                <Bar
                  data={budgetData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top" as const,
                        labels: { color: t.text },
                      },
                    },
                    scales: {
                      x: { ticks: { color: t.textSecondary } },
                      y: { ticks: { color: t.textSecondary } },
                    },
                  }}
                />
              </div>
            </div>

            {/* Campaign Timeline */}
            <div className="p-4 rounded-lg shadow border" style={{ background: t.surface, borderColor: t.border }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: t.text }}>Campaign Timeline</h3>
              <div className="h-[200px]">
                <Line
                  data={timelineData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top" as const,
                        labels: { color: t.text },
                      },
                    },
                    scales: {
                      x: { ticks: { color: t.textSecondary } },
                      y: { ticks: { color: t.textSecondary }, beginAtZero: true },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stats;
